import { useState } from "react";
import { AxiosError } from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LoginForm, ProFormText } from "@ant-design/pro-components";
import {
  LockOutlined,
  SafetyOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Alert, App as AntdApp, Button, Space, theme } from "antd";

import { login, ssoLogin } from "./api";
import BrandLogo from "@/components/BrandLogo";
import CaptchaWidget from "@/components/CaptchaWidget";
import {
  CAPTCHA_PROMPT,
  CAPTCHA_REQUIRED_DETAIL,
  useCaptchaConfig,
  useCaptchaToken,
} from "@/lib/captcha";
import { useCapabilities } from "@/lib/capabilities";
import type { LoginRequest } from "./types";
import { useAuthStore } from "@/stores/authStore";
import { apiErrorMessage } from "@/lib/errors";

// Exact backend 401 details for the MFA leg of login (auth_service.py). These
// must be matched verbatim BEFORE the generic error mapping: apiErrorMessage
// glosses any 401 as "your session may have expired", which misdiagnosed an
// MFA challenge as a session problem and locked MFA users out (F2).
const MFA_REQUIRED_DETAIL = "MFA code required";
const MFA_INVALID_DETAIL = "Invalid MFA code";

/** Copy shown when the account needs a one-time code (info, not an error). */
export const MFA_PROMPT =
  "Two-factor authentication is enabled on this account. Enter the 6-digit code from your authenticator app, or one of your backup codes.";

/** Copy for a wrong/expired one-time code — distinct from "code missing". */
export const MFA_INVALID_MESSAGE =
  "That code didn't work — enter the current code from your authenticator app, or a backup code.";

function loginErrorDetail(error: unknown): string | null {
  if (!(error instanceof AxiosError)) return null;
  const data = error.response?.data as { detail?: unknown } | undefined;
  return typeof data?.detail === "string" ? data.detail : null;
}

function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { message } = AntdApp.useApp();
  // Follow the app theme (persisted preference / OS fallback) instead of a
  // hardcoded light backdrop — this page renders before login too.
  const { token } = theme.useToken();
  const setSession = useAuthStore((state) => state.setSession);
  const capabilities = useCapabilities();
  // SASE-30: the standalone SASE edition has no self-service sign-up (an
  // administrator makes accounts) and is not the cloud.
  const saseEdition = capabilities.sase?.edition === "sase";
  const [submitting, setSubmitting] = useState(false);
  // Revealed after the backend answers "MFA code required": the same form
  // resubmits with `mfa_code` attached.
  const [mfaRequired, setMfaRequired] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // CAPTCHA (when the operator turned it on): every sign-in, or once the
  // backend answers "CAPTCHA required" after repeated failures for the name.
  const captcha = useCaptchaConfig();
  const captchaToken = useCaptchaToken();
  const [captchaAsked, setCaptchaAsked] = useState(false);
  const showCaptcha =
    captcha !== null && (captcha.loginAfterFailures === 0 || captchaAsked);

  const handleSso = async () => {
    try {
      const { authorize_url } = await ssoLogin();
      window.location.href = authorize_url;
    } catch {
      message.error("SSO is not configured for this deployment");
    }
  };

  const handleSubmit = async (values: LoginRequest) => {
    if (showCaptcha && !captchaToken.token) {
      setFormError(CAPTCHA_PROMPT);
      return;
    }
    const usedToken = captchaToken.token;
    setSubmitting(true);
    try {
      // Domain is an OpenStack concept customers shouldn't see; default it.
      const response = await login({
        ...values,
        domain: values.domain ?? "Default",
        ...(usedToken ? { captcha_token: usedToken } : {}),
      });
      setSession({
        accessToken: response.access_token,
        expiresIn: response.expires_in,
        user: response.user,
      });
      setFormError(null);
      setMfaRequired(false);
      message.success(`Welcome, ${response.user.email}`);
      const next = searchParams.get("next") ?? "/";
      navigate(next, { replace: true });
    } catch (error) {
      const detail = loginErrorDetail(error);
      // A CAPTCHA token is single-use: any answer that consumed one needs a new one.
      if (usedToken) captchaToken.reset();
      if (detail === CAPTCHA_REQUIRED_DETAIL) {
        setCaptchaAsked(true);
        setFormError(CAPTCHA_PROMPT);
      } else if (detail === MFA_REQUIRED_DETAIL) {
        // Credentials were right — reveal the one-time-code input.
        setMfaRequired(true);
        setFormError(null);
      } else if (detail === MFA_INVALID_DETAIL) {
        setMfaRequired(true);
        setFormError(MFA_INVALID_MESSAGE);
      } else if (
        error instanceof AxiosError &&
        error.response?.status === 401
      ) {
        // A login-time 401 is bad credentials, not an expired session — skip
        // apiErrorMessage's generic 401 gloss ("session may have expired").
        setFormError(detail ?? "Incorrect username or password.");
      } else {
        // Anything else (rate limit 429, network, 5xx) keeps the shared
        // mapping and its suggested actions.
        setFormError(apiErrorMessage(error, "Login failed. Please try again."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: token.colorBgLayout,
      }}
    >
      <LoginForm<LoginRequest>
        logo={<BrandLogo />}
        title=""
        subTitle={saseEdition ? "Secure access for your organisation" : "Cloud compute, on demand"}
        initialValues={{ domain: "Default" }}
        onFinish={async (values) => {
          await handleSubmit(values);
          return true;
        }}
        submitter={{
          searchConfig: { submitText: mfaRequired ? "Verify" : "Sign in" },
          submitButtonProps: { loading: submitting },
        }}
        actions={
          <Space direction="vertical" style={{ width: "100%" }}>
            {/* Rendering this unconditionally meant every customer saw a
                sign-in option that could only ever fail with "SSO is not
                configured". */}
            {capabilities.sso ? (
              <Button block onClick={() => void handleSso()}>
                Single sign-on (SSO)
              </Button>
            ) : null}
            <Space
              style={{ width: "100%", justifyContent: "space-between" }}
            >
              {saseEdition ? (
                <span />
              ) : (
                <Link to="/register">New here? Create an account</Link>
              )}
              <Link to="/forgot-password">Forgot password?</Link>
            </Space>
          </Space>
        }
      >
        {/* antd Alert carries role="alert", so both states are announced. */}
        {formError && (
          <Alert
            type="error"
            showIcon
            message={formError}
            style={{ marginBottom: 24 }}
          />
        )}
        {mfaRequired && !formError && (
          <Alert
            type="info"
            showIcon
            message={MFA_PROMPT}
            style={{ marginBottom: 24 }}
          />
        )}
        <ProFormText
          name="username"
          fieldProps={{ size: "large", prefix: <UserOutlined /> }}
          placeholder="Email"
          rules={[{ required: true, message: "Please enter your username" }]}
        />
        <ProFormText.Password
          name="password"
          fieldProps={{ size: "large", prefix: <LockOutlined /> }}
          placeholder="Password"
          rules={[{ required: true, message: "Please enter your password" }]}
        />
        {mfaRequired && (
          <ProFormText
            name="mfa_code"
            fieldProps={{
              size: "large",
              prefix: <SafetyOutlined />,
              // TOTP codes are 6 digits; backup codes are 10 hex chars.
              maxLength: 10,
              autoFocus: true,
              autoComplete: "one-time-code",
              inputMode: "numeric",
            }}
            placeholder="One-time code"
            rules={[{ required: true, message: "Please enter your one-time code" }]}
          />
        )}
        {showCaptcha && captcha ? (
          <CaptchaWidget
            provider={captcha.provider}
            siteKey={captcha.siteKey}
            onToken={captchaToken.setToken}
            resetKey={captchaToken.resetKey}
          />
        ) : null}
      </LoginForm>
    </div>
  );
}

export default LoginPage;
